import { ApolloLink, Observable } from '@apollo/client';
import { omit } from 'lomit';
import { graphql } from 'graphql-anywhere/lib/async';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const contentful = require('contentful');

import { contentfulResolver, graphqlParser } from './parsers';
import { buildDefinitionMap, getRootKey, parseQueryVariables } from './utils';

/**
 * ApolloLink instance that allows for queries to be performed against
 * the Contentful REST API.
 *
 * @returns {ContentfulRestLink}
 */
class ContentfulRestLink extends ApolloLink {
  constructor(clientOptions, queryDefaults = {}) {
    super();
    this.clientOptions = clientOptions;
    this.queryDefaults = queryDefaults; // Create Delivery Client

    this.client = contentful.createClient({ ...omit(clientOptions, ['previewAccessToken'])
    }); // Create Preview Client, if required options are passed
    // eslint-disable-next-line no-prototype-builtins

    if (clientOptions.hasOwnProperty('previewAccessToken') && clientOptions.previewAccessToken) {
      this.previewClient = contentful.createClient({ ...omit(clientOptions, ['previewAccessToken']),
        accessToken: clientOptions.previewAccessToken,
        host: 'preview.contentful.com'
      });
    }
  }
  /**
   * Handle ApolloLink.request
   *
   * @param {Operation} operation
   * @param {NextLink} forward
   * @return {Observable<FetchResult> | null}
   */


  request(operation, forward) {
    const {
      query,
      operationName
    } = operation;
    const obs = forward ? forward(operation) : Observable.of({
      data: {}
    });
    return obs.flatMap(({
      errors
    }) => new Observable(observer => {
      // Find name to apply as root field of the GraphQL data
      const rootKey = getRootKey(operation); // Set queryArgs based on variables/queryMethod

      const queryVariables = parseQueryVariables(operation); // Define the query method to use based on variables

      const queryMethod = // eslint-disable-next-line no-prototype-builtins
      queryVariables && queryVariables.hasOwnProperty('id') ? 'getEntry' : 'getEntries'; // Choose client based on `preview` variable

      const usePreview = this.previewClient && queryVariables && // eslint-disable-next-line no-prototype-builtins
      queryVariables.hasOwnProperty('preview') && queryVariables.preview === true;
      const client = usePreview ? this.previewClient : this.client; // Check the client has been defined

      if (!client) {
        observer.error(new Error('Apollo client is null'));
      } // Contentful query
      // Define query arguments based on the set method


      (queryMethod === 'getEntry' ? client === null || client === void 0 ? void 0 : client.getEntry(queryVariables.id, { ...this.queryDefaults,
        ...omit(queryVariables, ['id', 'preview'])
      }) : client === null || client === void 0 ? void 0 : client.getEntries({ ...this.queryDefaults,
        ...omit(queryVariables, ['preview']),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        content_type: rootKey && rootKey.replace('Collection', '') // eslint-disable-next-line @typescript-eslint/no-explicit-any

      })).then(contentfulData => {
        // Build definitionMap to supply expected shape of GraphQL query response
        const definitionMap = buildDefinitionMap(operation); // Parse Contentful data to expected GraphQL shape

        const parsedData = graphqlParser(rootKey || '', contentfulData, definitionMap[operationName]); // Query contentfulData via GraphQL query

        return graphql(contentfulResolver, query, parsedData);
      }) // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(data => {
        observer.next({
          data,
          errors
        });
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    }));
  }

}

export default ContentfulRestLink;